import { SasTokenPrefix } from "@services/user/types";

export interface CreateDeficiencyRequestDocument {
  documentUrl: string;
  documentName?: string;
}

export interface DeficiencyDocument {
  documentUrl: string;
  documentName?: string;
}

export const DEFICIENCY_DOCUMENT_PREFIXES = [
  SasTokenPrefix.VESSEL_REGISTRATION,
  SasTokenPrefix.DEFICIENCY,
] as const;

export interface BerthVisitedCode {
  berthCode: string;
  berthName: string;
  berthLength?: number;
  berthTypeCode?: string;
  quayCode?: string;
  meterMarkFrom?: number;
  meterMarkTo?: number;
  berthDwt?: number;
  berthDraftMax?: number;
  active?: boolean;
}

export interface DeficiencyCompany {
  companyCode: string;
  companyNameEnglish: string;
}

export interface CreateDeficiencyRequestPayload {
  vesselImoNumber: number;
  vesselName: string;
  shippingAgency: string;
  tripNoPlda: string;
  berthVisitedCode: string;
  loa: number | null;
  deficiencyDetails: string;
  remarks?: string;
  documents: CreateDeficiencyRequestDocument[];
}

export interface DeficiencyResolutionPayload {
  explanation: string;
  documents: DeficiencyDocument[];
}

export type DeficiencyDecisionStatus = "APPROVED" | "REJECTED";

export interface DeficiencyCompletePayload {
  status: DeficiencyDecisionStatus;
  approvalNotes?: string;
  approvalDocuments?: DeficiencyDocument[];
  rejectionNotes?: string;
  rejectionDocuments?: DeficiencyDocument[];
}

export interface DeficiencyTaskDecision {
  status: DeficiencyDecisionStatus;
  approvalNotes?: string | null;
  approvalDocuments?: DeficiencyDocument[] | null;
  rejectionNotes?: string | null;
  rejectionDocuments?: DeficiencyDocument[] | null;
  submittedBy?: string | null;
}

export type DeficiencyStatus =
  | "PENDING_RESOLUTION"
  | "PENDING_APPROVAL"
  | "AWAITING_APPROVAL"
  | "RESOLVED"
  | string;

export type DeficiencyTaskStatus =
  | "READY"
  | "CLAIMED"
  | "COMPLETED"
  | "CANCELLED"
  | "TERMINATED"
  | "UPDATED"
  | "UNCLAIMED";

export interface DeficiencySearchRequest {
  refOrderNumber?: number[];
  imoNumber?: number[];
  vesselName?: string[];
  companyCode?: string[];
  shippingAgency?: string[];
  tripNoPlda?: string[];
  berthVisitedCode?: string[];
  loa?: number[];
  details?: string[];
  createdAt?: Array<{
    from?: string;
    to?: string;
  }>;
  createdBy?: string[];
  status?: DeficiencyStatus[];
  resolutionCreatedAt?: Array<{
    from?: string;
    to?: string;
  }>;
  approvedRejectedAt?: Array<{
    from?: string;
    to?: string;
  }>;
  approvedRejectedBy?: string[];
}

export interface DeficiencyGridItem {
  deficiencyId: string;
  refOrderNumber: string;
  imoNumber: number;
  vesselName: string;
  shippingAgency: string;
  tripNoPlda: string;
  berthVisited: string;
  loa: number | null;
  details: string;
  createdAt: string;
  createdBy: string;
  resolutionCreatedAt: string | null;
  approvedRejectedAt: string | null;
  approvedRejectedBy: string | null;
  status: DeficiencyStatus;
}

export interface DeficiencySearchResponse {
  items: DeficiencyGridItem[];
  pageNumber: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
}

export interface DeficiencyDetailsResolution {
  id: string;
  explanation: string;
  documents: DeficiencyDocument[];
  createdAt: string;
  createdBy: string;
}

export interface DeficiencyTask {
  taskId: string;
  workflowId: string;
  runId: string;
  role?: string | null;
  createdAt: string;
  status: DeficiencyTaskStatus;
  claimOwnerName?: string | null;
}

export interface DeficiencyDetailsResponse {
  id: string;
  refOrderNumber: string | number;
  status: DeficiencyStatus;
  companyId?: string;
  vesselId: string;
  tripNoPlda: string;
  berthVisitedCode?: string;
  details: string;
  remarks?: string | null;
  createdAt: string;
  createdBy?: string;
  modifiedAt?: string | null;
  modifiedBy?: string | null;
  documents: DeficiencyDocument[];
  resolution?: DeficiencyDetailsResolution;
  task?: DeficiencyTask | null;
  taskDecision?: DeficiencyTaskDecision | null;
  vesselName: string;
  vesselImoNumber: string | number;
  loa: string | number | null;
  berthVisited?: string;
}
